import * as THREE from 'three';
import { SnapshotBuffer } from '../net/interpolation';
import type { RemoteState } from '../net/protocol';
import type { CharacterLook } from '../state/types';
import { buildCargo, buildMower } from '../systems/Jobs';
import { buildCharacter, CharacterAnimator, type CharacterRig } from '../world/builders/character';
import { beamCone, lampProp } from '../world/Lamp';
import type { NpcHold } from './NpcManager';
import type { RideId } from '../content/rides';
import { buildRascal, type BuiltRascal } from '../world/builders/rascal';
import { buildRide } from '../world/builders/rides';
import type { BuiltCar } from '../world/builders/vehicles';

const WHEEL_RADIUS = 0.34;

/**
 * Another player's character in our world. It replays their movement from the network
 * (slightly in the past, for smoothness) and swaps to their Camaro while they drive.
 * Remote players are ghosts: nothing collides with them.
 */
export class RemoteAvatar {
  readonly buffer = new SnapshotBuffer();
  readonly rig: CharacterRig;
  /** What's being drawn this frame, or null before their first update arrives. */
  state: RemoteState | null = null;
  private readonly anim: CharacterAnimator;
  private readonly mower = buildMower();
  private readonly cargo = buildCargo('junk');
  private readonly carRoot = new THREE.Group();
  private car: BuiltCar | null = null;
  private ride: RideId = 'camaro';
  /** While set, we're in a challenge with them: they sit where it says, whatever the network says. */
  hold: NpcHold | null = null;
  /** Their headlamp and its beam (no real light: one spotlight per player would cost too much). */
  private readonly lamp = lampProp('headlamp');
  private readonly beam = beamCone(6, 1.8);
  /** Their Lil' Rascal, built the first time we see them riding one. */
  private rascal: BuiltRascal | null = null;
  private readonly parent: THREE.Object3D;

  /** What they drive; the model swaps next time they get in. */
  setRide(ride: RideId): void {
    if (ride === this.ride) return;
    this.ride = ride;
    if (this.car) {
      this.carRoot.remove(this.car.group);
      this.car.group.traverse((o) => {
        if (o instanceof THREE.Mesh) o.geometry.dispose();
      });
      this.car = null;
    }
  }

  constructor(parent: THREE.Object3D, look: CharacterLook) {
    this.rig = buildCharacter(look);
    this.anim = new CharacterAnimator(this.rig);
    // Job props: they push a mower or haul junk, so do they here.
    this.mower.position.set(0, 0, 0.9);
    this.cargo.scale.setScalar(0.85);
    this.mower.visible = this.cargo.visible = false;
    this.rig.root.add(this.mower);
    this.rig.carry.add(this.cargo);
    this.rig.root.visible = this.carRoot.visible = false;
    this.lamp.visible = false;
    this.rig.head.add(this.lamp);
    this.beam.visible = false;
    this.beam.position.set(0, 1.85, 0.2);
    this.beam.rotation.x = 0.2;
    this.rig.root.add(this.beam);
    parent.add(this.rig.root, this.carRoot);
    this.parent = parent;
  }

  /** Where they're standing right now, if we've heard from them. */
  at(): { x: number; z: number } | null {
    return this.state ? { x: this.state.x, z: this.state.z } : null;
  }

  /** Where to pin their name tag, or null when they shouldn't be labeled. */
  labelAnchor(out: THREE.Vector3): THREE.Vector3 | null {
    const s = this.state;
    if (!s || s.asleep) return null;
    if (s.driving) return out.set(s.carX, 2, s.carZ);
    if (s.hidden) return null;
    return out.set(s.x, s.y + 2.1 * this.rig.look.height, s.z);
  }

  update(dt: number, time: number, now: number): void {
    const s = this.buffer.sample(now);
    this.state = s;
    const root = this.rig.root;
    const h = this.hold;
    if (h) {
      root.visible = true;
      this.carRoot.visible = false;
      if (this.rascal) this.rascal.group.visible = false;
      root.position.set(h.x, 0, h.z);
      root.rotation.y = h.heading;
      this.anim.update({ dt, time, speed: 0, pose: h.pose, seat: h.seat });
      h.after?.(this.rig, time);
      return;
    }
    this.carRoot.visible = !!s?.driving;
    if (s?.driving) this.driveCar(s);
    root.visible = !!s && !s.hidden && !s.driving && !s.asleep;
    const scooting = root.visible && (s?.pose === 'scoot' || s?.pose === 'scootdrink');
    if (scooting && !this.rascal) {
      this.rascal = buildRascal();
      this.parent.add(this.rascal.group);
    }
    if (this.rascal) {
      this.rascal.group.visible = scooting;
      if (scooting && s) {
        this.rascal.group.position.set(s.x, 0, s.z);
        this.rascal.group.rotation.y = s.facing;
        this.rascal.flag.rotation.y = -1.2 + Math.sin(time * 9) * 0.2;
      }
    }
    if (!s || !root.visible) return;
    root.position.set(s.x, s.y, s.z);
    root.rotation.y = s.facing;
    this.anim.update({
      dt,
      time,
      speed: s.speed,
      pose: s.pose,
      sprinting: s.sprint,
      airborne: s.airborne,
      seat: s.seat,
    });
    if (s.flip !== 0) this.rig.body.rotation.x = s.flip;
    this.mower.visible = s.pose === 'push';
    this.cargo.visible = s.pose === 'carry';
    this.lamp.visible = this.beam.visible = s.lamp;
  }

  dispose(): void {
    for (const obj of [this.rig.root, this.carRoot, ...(this.rascal ? [this.rascal.group] : [])]) {
      obj.removeFromParent();
      // Materials and textures are shared caches; only the geometry is ours.
      obj.traverse((o) => {
        if (o instanceof THREE.Mesh) o.geometry.dispose();
      });
    }
  }

  private driveCar(s: RemoteState): void {
    if (!this.car) {
      this.car = buildRide(this.ride);
      this.carRoot.add(this.car.group);
      this.carRoot.position.set(s.carX, 0, s.carZ);
    }
    // The car faces +x locally: forward is (cos h, -sin h). Roll the wheels by distance driven.
    const p = this.carRoot.position;
    const ahead = (s.carX - p.x) * Math.cos(s.carHeading) - (s.carZ - p.z) * Math.sin(s.carHeading);
    if (Math.abs(ahead) < 5) for (const w of this.car.wheels) w.rotation.y -= ahead / WHEEL_RADIUS;
    p.set(s.carX, 0, s.carZ);
    this.carRoot.rotation.y = s.carHeading;
  }
}
